import type { BrandSlug } from '../shared/brand-slug';
import type { EAN } from '../shared/ean';
import type { OrgId } from '../shared/ids';

export type SkuQuota = {
  /** All unique EANs the org has access to (full entitlement count). */
  readonly total: number;
  /** EANs with both a product entry and sales data — the headline count. */
  readonly active: number;
  readonly max: number;
};

export type CategorySubscription = {
  readonly category: string;
  readonly includedBrands: readonly BrandSlug[];
};

/** Source of access: brand access (via category subscription), a Segment subscription, or both. */
export type SubscriptionCause = 'brand' | 'segment' | 'both';

export type SubscribedSku = {
  readonly ean: EAN;
  readonly cause: SubscriptionCause;
  /** Backend-computed: has processed sales data; usable in portal views. */
  readonly hasSalesData: boolean;
  /** Backend-computed: has a product entry in medpdb2. */
  readonly hasCatalogEntry: boolean;
  /** Backend-computed: seen in the latest vendor feeds (currently listed). */
  readonly listedAtVendor: boolean;
};

export type Coverage = {
  readonly kind: 'coverage';
  readonly orgId: OrgId;
  readonly categorySubscriptions: ReadonlyMap<string, CategorySubscription>;
  readonly subscribedSkus: ReadonlyMap<EAN, SubscribedSku>;
  readonly quota: SkuQuota;
};

export const emptySkuQuota: SkuQuota = { total: 0, active: 0, max: 0 };

export function emptyCoverage(orgId: OrgId): Coverage {
  return {
    kind: 'coverage',
    orgId,
    categorySubscriptions: new Map(),
    subscribedSkus: new Map(),
    quota: emptySkuQuota,
  };
}

export function subscribedCategorySlugs(coverage: Coverage): ReadonlySet<string> {
  return new Set(coverage.categorySubscriptions.keys());
}

export function isCategorySubscribed(coverage: Coverage, slug: string): boolean {
  return coverage.categorySubscriptions.has(slug);
}

/**
 * True if the slug is subscribed, or any ancestor is subscribed.
 * Slugs use '|' as the path separator.
 */
export function isEffectivelySubscribed(coverage: Coverage, slug: string): boolean {
  if (coverage.categorySubscriptions.has(slug)) return true;
  for (const subscribed of coverage.categorySubscriptions.keys()) {
    if (slug.startsWith(`${subscribed}|`)) return true;
  }
  return false;
}

export function brandsUnlockedBy(coverage: Coverage, category: string): readonly BrandSlug[] {
  return coverage.categorySubscriptions.get(category)?.includedBrands ?? [];
}

export function skuHasSalesData(coverage: Coverage, ean: EAN): boolean {
  return coverage.subscribedSkus.get(ean)?.hasSalesData === true;
}

export function quotaRemaining(coverage: Coverage): number {
  return coverage.quota.max - coverage.quota.total;
}

export function usagePercent(coverage: Coverage): number {
  const max = coverage.quota.max;
  return max > 0 ? Math.round((coverage.quota.total / max) * 100) : 0;
}
